<template>
  <tr :class="{ checked: row.checked }">
    <td class="cell-business">
      <div :class="['d-flex position-relative', { 'disabled-cell': disabledState(row) }]">
        <Checkbox :value="row.checked" @input="onRowCheckToggle" />
        <div class="w-100">
          <a v-if="!row.deletedAt"
            :href='row.url'
            class="d-flex w-100 gap-2 pr-5 justify-content-between"
            @click.exact.prevent.stop="showConfig(row.obfuscatedId)">
            {{ row.trackedBusiness }}
            <span v-if="row.archived" class="badge badge-fixed badge-pill badge-secondary badge-rounded">
              <span class="wrap-icon">
                <i class="fa-solid fa-box-archive" />
              </span>
              Archived
            </span>
            <template v-if="row.notEnoughCreditsError">
              <div class="geogrid-error-badge mb-2" id="not-enough-credits-icon">
                <i class="fa-solid fa-wallet" id="not-enough-credits-icon" />
                Not processed
              </div>
              <b-popover
                placement='top'
                target="not-enough-credits-icon"
                custom-class='geogrids-popover p-1'
                triggers='hover'>
                {{ notEnoughtCreditsWarning }}
              </b-popover>
            </template>
          </a>
          <span v-else>{{ row.trackedBusiness }}</span>
          <small class='d-block' v-show='row.address'>{{ row.address }}</small>
          <Tags :ref='`${row.obfuscatedId}-tags`' :initial-tags='row.tags' :searchable-tags='true'
            @addTagToFilter='addTagToFilter' />
          <CategoriesList v-if="row.businessCategories?.length" :categories="row.businessCategories" />
          <div v-if='row.labels' class='location-labels'>
            <span v-for='label in row.labels' :key='`${label}${row.obfuscatedId}`' class='location-labels-item'>
              {{ label }}
            </span>
          </div>
        </div>
        <img v-if="row.totalGeogrids.top3AppearedCount && row.totalGeogrids.newTop3AppearedCount > 0"
          :src="require('top3_appeared_count.svg')" class="top3-appeared-count" alt="">
        <img v-else-if="row.totalGeogrids.newTop3AppearedCount > 0" :src="require('top3_appeared_count_green.svg')"
          class="top3-appeared-count" alt="">
        <img v-else-if="row.totalGeogrids.top3AppearedCount" :src="require('top3_appeared_count_blue.svg')"
          class="top3-appeared-count" alt="">
      </div>
    </td>
    <td class="cell-grid">
      <span :class="[{ 'disabled-cell': disabledState(row) }]">
        {{ row.grid }}
      </span>
    </td>
    <td class="cell-schedule">
      <span :class="[{ 'disabled-cell': disabledState(row) }]">
        {{ row.scheduleFormatted }}
      </span>
    </td>
    <td class="cell-last-geogrid">
      <span :class="[{ 'disabled-cell': disabledState(row) }]">
        {{ row.lastGenerated }}
      </span>
    </td>
    <td class="cell-next-creation">
      <span :class="[{ 'disabled-cell': disabledState(row) }]">
        {{ row.nextGeneration ?? 'Not specified' }}
        <small v-if="!linda && row.nextGeneration" class="d-block">
          <i class='far fa-wallet pr-1' />
          {{ row.credits }} credits
        </small>
      </span>
    </td>
    <td class="cell-geogrids">
      <div :class="[{ 'disabled-cell': disabledState(row) }]">
        <div v-if="!row.deletedAt">
          <i class='far fa-th pr-1' />
          {{ row.totalGeogrids.totalGeogrids }} geogrids
        </div>
        <small v-if="row.totalGeogrids.inactive === 0">{{ row.totalGeogrids.active }} keywords</small>
        <div v-else>
          <small class="d-block">{{ row.totalGeogrids.active }} enabled keywords</small>
          <small class="d-block">{{ row.totalGeogrids.inactive }} disabled keywords</small>
        </div>
      </div>
    </td>
    <td class="cell-actions">
      <div v-if="!row.deletedAt" class='btn-group ml-2'>
        <button @click='getDropdownDirection' type='button' class='btn-context m-auto d-block'
          data-toggle='dropdown'
          aria-haspopup='true' aria-expanded='false'>
          <span class='wrap-icon'><i class='far fa-ellipsis-h' /></span>
        </button>
        <div class='dropdown-menu dropdown-menu-right geogrids-table-dropdown py-0'>
          <a class="action-block" :href="row.publicUrl" target="_blank" :id="`open-public-config-${row.obfuscatedId}`">
            <span class="wrap-icon">
              <i class="far fa-globe" />
            </span>
            Open public page
          </a>
          <span class='dropdown-divider' />
          <div class="action-block">
            <BtnCopyLinkToClipboard classes='btn btn-link' entity-name='config' placement='left' :link='row.url' />
            Copy link
          </div>
          <span class='dropdown-divider' />
          <button :id="`edit-config-${row.obfuscatedId}`" :class="['action-block', { disabled: isEditingDisabled }]"
            @click="showConfigsEditTab(row.obfuscatedId)">
            <span class="wrap-icon">
              <i class='far fa-cog' />
            </span>
            Edit config
          </button>
          <span class='dropdown-divider' />
          <a :href="row.comparisonUrl" class='action-block' target="_blank" :id="`compare-config-${row.obfuscatedId}`">
            <span class="wrap-icon">
              <i class='far fa-crosshairs fa-fw' />
            </span>
            Compare geogrids
          </a>
          <b-popover v-if="gmbAdmin"
            placement='left'
            :target="`compare-config-${row.obfuscatedId}`"
            custom-class='geogrids-popover p-1'
            triggers='hover'>
            <a href="#" @click.prevent="showModal(row)">Configure Comparison Link</a>
          </b-popover>

          <b-modal v-model="modalVisible" title="Configure Comparison Link" centered hide-footer>
            <b-form-group label="Date From (optional)">
              <div @click.stop>
                <DatePicker v-model="minDate" :value="minDate" max-date="today" />
              </div>
            </b-form-group>

            <b-form-group label="Date To (optional)">
              <div @click.stop>
                <DatePicker v-model="maxDate" :value="maxDate" max-date="today" />
              </div>
            </b-form-group>
            <div class="d-flex justify-content-between mt-3">
              <b-button class="mt-2" @click="copyComparisonLink">
                <i class="far fa-fw fa-copy" /><span>Copy direct link</span>
              </b-button>
              <b-button class="mt-2" @click="modalVisible = false">
                Close
              </b-button>
            </div>
          </b-modal>

          <span class='dropdown-divider' />
          <button :id="`archive-config-${row.obfuscatedId}`" :class="['action-block', { disabled: isEditingDisabled }]"
            @click='changeArchivedState(row)'>
            <span class="wrap-icon">
              <i class='far fa-box-archive' />
            </span>
            {{ row.archived ? 'Unarchive' : 'Archive' }} config
          </button>
          <span class='dropdown-divider' />
          <button :id="`delete-config-${row.obfuscatedId}`"
            :class="['action-block text-danger', { disabled: isEditingDisabled }]" @click='deleteConfig(row)'>
            <span class="wrap-icon">
              <i class='far fa-trash text-danger' />
            </span>
            Delete config
          </button>
        </div>
      </div>
      <div v-else class="d-flex flex-column gap-2">
        <button
          class='btn btn-link'
          :id="`restore-config-${row.obfuscatedId}`"
          @click="restoreConfig(row)">
          <i class="far fa-sync text-danger" />
        </button>
        <b-popover placement='left' :target="`restore-config-${row.obfuscatedId}`" custom-class='geogrids-popover p-1'
          triggers='hover'>
          <span class='text-danger'>Restore config</span>
        </b-popover>
      </div>
    </td>
  </tr>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import DatePicker from 'vue_widgets/components/datepicker'
import { copyToClipboardWithAnimation } from 'common/copy_to_clipboard'
import Tags from '../../components/tags'
import CategoriesList from '../../components/categories_list'
import Checkbox from '../../../components/checkbox'
import BtnCopyLinkToClipboard from '../../../components/copy_to_clipboard'
import xorEncrypt from '../../../../helpers/encryption'

const KEY = 'LocalViking_one_love'

export default {
  components: {
    Tags, CategoriesList, Checkbox, BtnCopyLinkToClipboard, DatePicker
  },
  props: {
    row: { type: Object, required: true },
    apiConfigsPath: { type: String, required: true }
  },
  data() {
    return {
      modalVisible: false,
      minDate: null,
      maxDate: null,
      key: KEY,
      gmbAdmin: Styxie?.USER?.gmbAdmin
    }
  },
  created() {
    this.isEditingDisabled = !Styxie?.USER?.editor
  },
  computed: {
    ...mapState(['notEnoughtCreditsWarning', 'linda']),
    payload() {
      let from = this.minDate?.[0] ?? null
      let to = this.maxDate?.[0] ?? null

      if (from && to && new Date(from) > new Date(to)) {
        [from, to] = [to, from]
      }

      return {
        id: this.row.obfuscatedId,
        by_period: [from, to]
      }
    },
    generateComparisonLink() {
      return `${window.location.origin}/stats/gmb/geogrid_configs/${this.generateEncryptedToken}/compare`
    },
    generateEncryptedToken() {
      return xorEncrypt(JSON.stringify(this.payload), this.key)
    }
  },
  methods: {
    ...mapActions(['showConfig', 'showConfigsEditTab']),
    addTagToFilter(tag) {
      this.$emit('addTagToFilter', tag)
    },
    changeArchivedState(row) {
      const action = row.archived ? 'reveal' : 'archive'
      axios.post(`${this.apiConfigsPath}/${row.obfuscatedId}/${action}`).then(() => {
        toastr.success(`Config successfully ${action}${row.archived ? 'ed' : 'd'}.`)
        this.$emit('onDeleteRow')
      })
    },
    deleteConfig(row) {
      swal({
        text: 'This will also lead to deletion of all generated geogrids',
        icon: 'warning',
        buttons: ['Cancel', 'Delete all'],
        dangerMode: true
      }).then((result) => {
        if (result) {
          axios.delete(`${this.apiConfigsPath}/${row.obfuscatedId}`).then(() => {
            toastr.success('Config successfully deleted.')
            this.$emit('onDeleteRow')
          })
        }
      })
    },
    restoreConfig(row) {
      this.$emit('restoreGeogrids', [row.obfuscatedId])
    },
    disabledState(row) {
      if (row.archived) return true
      return !row.nextGeneration && !(row.scheduleFormatted === 'On Demand Run')
    },
    onRowCheckToggle(bool) {
      this.$emit('onRowCheckToggle', { bool: bool, configId: this.row.obfuscatedId })
    },
    showModal() {
      this.modalVisible = true
      this.minDate = null
      this.maxDate = null
    },
    copyComparisonLink(e) {
      copyToClipboardWithAnimation({ element: e.currentTarget, textToCopy: this.generateComparisonLink })
    },
    getDropdownDirection(event) {
      const btn = event.currentTarget
      btn.nextElementSibling.classList.toggle('up', window.innerHeight - btn.getBoundingClientRect().top < 320)
    }
  }
}
</script>
